/**
 * deadStockDataSource.ts — WatermelonDB → DeadStockInput bridge.
 *
 * Loads every physical, inventory-tracked product that currently holds stock,
 * then their net sales over the last `windowDays` (SALE minus CREDIT_NOTE units)
 * and the most recent sale date. Services and untracked items are skipped —
 * they have no stock to lock capital.
 */

import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { fetchInChunks } from '@/db/fetchInChunks';
import { Invoice, LineItem, VOIDED_INVOICE_STATUSES } from '@/db/models/Billing';
import { Product, Category } from '@/db/models/Inventory';
import type { DeadStockInput } from './deadStock';

const CREDIT_NOTE = 'CREDIT_NOTE';

export async function loadDeadStock(
  storeId: string,
  windowDays = 90,
  slowCoverDays = 180,
  asOf: Date = new Date(),
): Promise<DeadStockInput> {
  const windowStart = asOf.getTime() - windowDays * 24 * 60 * 60 * 1000;

  const products = await database.get<Product>('products').query(
    Q.where('store_id', storeId),
    Q.where('is_deleted', false),
    Q.where('is_service', false),
    Q.where('track_inventory', true),
    Q.where('current_stock', Q.gt(0)),
  ).fetch();

  // Category names.
  const categoryIds = [
    ...new Set(products.map((p) => p.categoryId).filter((id): id is string => !!id)),
  ];
  const categoryRows = await fetchInChunks(categoryIds, (batch) =>
    database.get<Category>('categories').query(
      Q.where('id', Q.oneOf(batch)),
      Q.where('is_deleted', false),
    ).fetch(),
  );
  const categoryName = new Map(categoryRows.map((c) => [c.id, c.name]));

  // Sales activity in the window: SALE + CREDIT_NOTE invoices, then their lines.
  const invoices = await database.get<Invoice>('invoices').query(
    Q.where('store_id', storeId),
    Q.where('is_deleted', false),
    Q.where('status', Q.notIn(VOIDED_INVOICE_STATUSES)),
    Q.where('type', Q.oneOf(['SALE', CREDIT_NOTE])),
    Q.where('created_at', Q.gte(windowStart)),
    Q.where('created_at', Q.lt(asOf.getTime())),
  ).fetch();

  const typeByInvoiceId = new Map(invoices.map((i) => [i.id, i.type]));
  const dateByInvoiceId = new Map(invoices.map((i) => [i.id, i.createdAt]));
  const invoiceIds = invoices.map((i) => i.id);

  const lineRows = await fetchInChunks(invoiceIds, (batch) =>
    database.get<LineItem>('line_items').query(
      Q.where('invoice_id', Q.oneOf(batch)),
      Q.where('is_deleted', false),
    ).fetch(),
  );

  // Net units sold + last SALE date per product.
  const sold = new Map<string, { units: number; lastSoldAt: Date | null }>();
  for (const li of lineRows) {
    const type = typeByInvoiceId.get(li.invoiceId) ?? 'SALE';
    const sign = type === CREDIT_NOTE ? -1 : 1;
    const acc = sold.get(li.productId) ?? { units: 0, lastSoldAt: null };
    acc.units += sign * li.quantity;
    if (type !== CREDIT_NOTE) {
      const d = dateByInvoiceId.get(li.invoiceId) ?? null;
      if (d && (!acc.lastSoldAt || d > acc.lastSoldAt)) acc.lastSoldAt = d;
    }
    sold.set(li.productId, acc);
  }

  return {
    windowDays,
    slowCoverDays,
    asOf,
    products: products.map((p) => {
      const s = sold.get(p.id);
      return {
        id: p.id,
        name: p.name,
        category: (p.categoryId && categoryName.get(p.categoryId)) || 'Uncategorised',
        currentStock: p.currentStock,
        purchasePrice: p.purchasePrice,
        sellingPrice: p.sellingPrice,
        unitsSold: s?.units ?? 0,
        lastSoldAt: s?.lastSoldAt ?? null,
      };
    }),
  };
}
